import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { useEffect, useRef, useState } from "react";
import { ColumnDef, OrderModel } from "../../models/orders/order-list-model";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import NewOrderReview from "./new-order-review";
import myOrderServices from "../../services/orders/myorder.service";
import { Tag } from "primereact/tag";
import { getSeverity } from "../../services/order/order-list.-mock";
import { Toast, ToastMessage } from "primereact/toast";
import UserDeleteDialog from "../edit-users/user-delete-dialog";
import { TabPanel, TabView } from "primereact/tabview";
import OrderPrint from "./new-order-print";
import { DataTableStateEvent } from "primereact/datatable";
import orderServices from "../../services/orders/orderservice";
import { InputTextarea } from "primereact/inputtextarea";
import OrderDeliveryAssignmentDialog from "./order-delivery-assignment-dialog";
import { getOrderListDisplayValue, getPendingProductCount } from "./order-review-calculations";
import OrderPaymentLinkButton from "./order-payment-link-button";

interface DataTableProps {
  data: any[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (event: DataTableStateEvent) => void;
  onOrderCancelled: () => void;
  onOrderReturned: () => void;
  onOrderAssigned?: () => void | Promise<void>;
  infoMessage?: string;
}

const OrderListDataTable: React.FC<DataTableProps> = ({ data, pageNumber, pageSize, totalRecords, onPageChange, onOrderCancelled,onOrderReturned, onOrderAssigned, infoMessage }) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [orderDataList, setOrderDataList] = useState<OrderModel[]>(data);
  const [visible, setVisible] = useState(false);
  const [dialogHeader, setDialogHeader] = useState(t("orderlist_orderdetails_label"));
  const [printEnabled, setPrintEnabled] =useState(false);
  const [selectedId, setSelectedId] = useState<number>(0);
  const [notifyHeader, setNotifyHeader] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [isNotifyVisible, setIsNotifyVisible] = useState(false);
  const [orderDetails, setOrderDetails] = useState<OrderModel | null>(null);
  const [actionType, setActionType] = useState<"cancel" | "return">("cancel");
  const [assignDialogVisible, setAssignDialogVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number>(0);
  const [commentDialog, setCommentDialog] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [sendingInvoiceMessageOrderIds, setSendingInvoiceMessageOrderIds] = useState<number[]>([]);
  const [paymentLinkOrderIds, setPaymentLinkOrderIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    let current = true;
    setPaymentLinkOrderIds(new Set());
    const ids = Array.from(new Set(data.map(row => Number(row.orderID))
      .filter(id => Number.isSafeInteger(id) && id > 0)));
    if (ids.length > 0) {
      orderServices.getOpenOrderPaymentLinkEligibility(ids).then(results => {
        if (current) setPaymentLinkOrderIds(new Set(results
          .filter(row => row.canSendPaymentLink === true).map(row => row.orderId)));
      }).catch(() => {
        // Fail closed: row status/payment text is not an authorization boundary.
        if (current) setPaymentLinkOrderIds(new Set());
      });
    }
    return () => { current = false; };
  }, [data]);

  const removePaymentLinkEligibility = (orderId: number) => {
    setPaymentLinkOrderIds(current => {
      const next = new Set(current);
      next.delete(orderId);
      return next;
    });
  };

  const handleSetOrderData = async (rowData: OrderModel) => {
    setVisible(true);
    try {
      const details = await orderServices.GetOrderByOrderId(rowData.orderID);
      if (details) {
        let exportedOrder: any = null;
        if (!(details.courierProviderName || details.courierProvider || (rowData as any).courierProviderName || (rowData as any).courierProvider)) {
          try {
            const exportRows = await orderServices.getorderExport({ orderIds: [rowData.orderID] });
            exportedOrder = Array.isArray(exportRows)
              ? exportRows.find((order: any) => Number(order.orderID ?? order.orderId) === Number(rowData.orderID))
              : null;
          } catch {
            // The detail view remains available if the export endpoint is unavailable.
          }
        }
        // The detailed order endpoint can omit courier fields that are present
        // on the list row/export data, so carry those through to the print view.
        setOrderDetails({
          ...details,
          courierProviderName: details.courierProviderName || details.courierProvider || rowData.courierProviderName || (rowData as any).courierProvider || exportedOrder?.courierProviderName || exportedOrder?.courierProvider,
          courierProviderID: details.courierProviderID ?? (details as any).courierProviderId ?? (rowData as any).courierProviderID ?? exportedOrder?.courierProviderID,
        });
      } else {
        console.warn("No details found for order ID:", rowData.orderID);
        showMessage("Warning", "No details found for this order", "warn");
      }
    } catch (error) {
      console.error("Error while fetching order details:", error);
      showMessage("Error", "Failed to fetch order details", "error");
    }
  };
 
   useEffect(() => {    
    if (orderDetails !== null) setVisible(true);
  }, [orderDetails]);
 
  useEffect(() => {
    setOrderDataList(data);
  },[data]);
 
  const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
    toast.current?.show({ severity: severity, summary: label, detail: message, life: 3000 });
  };
 
  const handleCancelOrder = (rowData: OrderModel): void => {
    setSelectedId(rowData.orderID);
    setActionType("cancel");
    setNotifyHeader("Cancel Order");
    setNotifyMessage("Are you sure you want to cancel this order?");
    setIsNotifyVisible(true);
  }

  const handleReturnOrder = (rowData: OrderModel): void => {
    setSelectedId(rowData.orderID);
    setActionType("return");
    setNotifyHeader("Return Order");
    setNotifyMessage("Are you sure you want to return this order?");
    setIsNotifyVisible(true);
  }

  const cancelOrder = async () => {
    try {
      let response = await myOrderServices.updateOrderStatus({
        orderID: selectedId,
        orderStatusID: 6,
      });
      if(response.isSuccess) {
        showMessage('Cancel Order','Order cancelled successfully', 'success');
        onOrderCancelled();
        const updatedData = updateStatusOnAction(selectedId, 6, "Cancelled");
        setOrderDataList(updatedData);
      }
      else
      {
        showMessage('Cancel Order', response.message + ': Error in cancel order', 'error');
      }
    }
    catch(error) {
      console.error(error);
    }
  };

  const returnOrder = async () => {
    try {
      let response = await myOrderServices.updateOrderStatus({
        orderID: selectedId,
        orderStatusID: 8,
      });
      if(response.isSuccess) {
        showMessage('Return Order','Order returned successfully', 'success');
        onOrderReturned();
        const updatedData = updateStatusOnAction(selectedId, 8, "Returned");
        setOrderDataList(updatedData);
      }
      else {
        showMessage('Return Order', response.message + ': Error in returning order', 'error');
      }
    }
    catch(error) {
      console.error(error);
    }
  };

  function updateStatusOnAction(id: number, statusId: number, statusName: string): OrderModel[] {
    return orderDataList.map((item: OrderModel) => {
      if (item.orderID === id) {
        return { ...item, orderStatusID: statusId, orderStatus: statusName };
      }
      return item;
    });
  }

  const hideNotifyDialog = (value: boolean) => {
    if (value) {
      if (actionType === "cancel") {
        cancelOrder();
      } else {
        returnOrder();
      }
    }
    setIsNotifyVisible(false);
  };

  const handleAssignOrder = async (rowData: OrderModel) => {
    setSelectedOrderId(rowData.orderID);
    setAssignDialogVisible(true);
  };

  const handleOpenReceipt = (invoiceNo?: string) => {
    if (!invoiceNo) return;
    const basePath = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
    const receiptUrl = `${window.location.origin}${basePath}/billing/orderdetails/${encodeURIComponent(invoiceNo)}`;
    window.open(receiptUrl, "_blank", "noopener,noreferrer");
  };

  const handleOpenOrderReceipt = (orderID?: number) => {
    if (!orderID || !Number.isFinite(orderID)) return;
    const basePath = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
    const receiptUrl = `${window.location.origin}${basePath}/billing/orderdetails/order-${encodeURIComponent(orderID)}`;
    window.open(receiptUrl, "_blank", "noopener,noreferrer");
  };

  const sendInvoicedWhatsAppMessage = async (rowData: OrderModel) => {
    if (!rowData.orderID || !rowData.invoiceNo || sendingInvoiceMessageOrderIds.includes(rowData.orderID)) return;

    setSendingInvoiceMessageOrderIds((current) => [...current, rowData.orderID]);
    try {
      await orderServices.sendWhatsAppTemplateMessage(rowData.orderID, `invoiced|${rowData.invoiceNo}`);
      showMessage("WhatsApp", `Invoiced message sent to ${rowData.userName}.`, "success");
    } catch (error) {
      console.error(`Failed to send invoiced WhatsApp message for order ${rowData.orderID}:`, error);
      showMessage("WhatsApp", `Failed to send invoiced message to ${rowData.userName}.`, "error");
    } finally {
      setSendingInvoiceMessageOrderIds((current) => current.filter((orderID) => orderID !== rowData.orderID));
    }
  };

  const openCommentDialog = async (rowData: OrderModel) => {
    setSelectedOrderId(rowData.orderID);
    const response = await orderServices.getOrderInstructions(rowData.orderID);

    if (response && response.orderComments) {
      setCommentText(response.orderComments);
    } else {
      setCommentText("");
    }

    setCommentDialog(true);
  };

  const submitComment = async () => {
    if (!commentText.trim()) {
      toast.current?.show({severity: "warn",summary: "No Comment",detail: "Please enter a comment."});
      return;
    }

    try {
      const response = await orderServices.addOrderComment(selectedOrderId, commentText);
      if (response.isSuccess) {
      toast.current?.show({severity: "success",summary: "Comment Updated",detail: response.message || "Your comment has been saved."});
      }
      setCommentDialog(false);
    } 
    catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Failed",
        detail: "Could not save comment"
      });
    }
  };

  const columns: ColumnDef[] = [
    {
      field: "orderID",
      header: t("orderlist_table_orderno"),
      style: { minWidth: "7rem" },
      cell: (row: any) => (
        <span
          style={{ color: "darkcyan", fontWeight: "bolder", cursor: "pointer" }}
          onClick={() => handleSetOrderData(row)}
        >
          {row.orderID}
        </span>
      ),
    },
    {
      field: "orderDate",
      header: t("orderlist_table_orderdate"),
      style: { minWidth: "9rem" },
      cell: (row: any) =>
        new Date(row.orderDate).toLocaleDateString("en-gb", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      field: "invoiceNo",
      header: t("orderlist_table_invoiceno"),
      style: { minWidth: "10rem" },
      cell: (row: any) => (
        row.invoiceNo ? (
          <span style={{ color: "darkcyan", fontWeight: "bolder" }}>
            {row.invoiceNo}
          </span>
        ) : (
          <span></span>
        )
      ),
    },
    {
      field: "invoiceDate",
      header: t("orderlist_table_invoicedate"),
      style: { minWidth: "10rem" },
      cell: (row: any) => (
        row.invoiceDate ? (
          new Date(row.invoiceDate).toLocaleDateString("en-gb", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        ) : (
          <span></span>
        )
      ),
    },
    {
      field: "orderStatus",
      header: t("orderlist_table_orderstatus"),
      style: { minWidth: "10rem" },
      cell: (row: any) => (
        <div style={{ textAlign: "center" }}>
          <Tag value={row.orderStatus} severity={getSeverity(row.orderStatusID)} />
        </div>
      ),
    },
    {
      field: "totalAmount",
      header: t("orderlist_table_totalvalue"),
      style: { minWidth: "9rem" },
      cell: (row: any) =>
        Math.round(getOrderListDisplayValue(row)).toLocaleString("en-gb", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      field: "pendingQuantity",
      header: t("orderlist_table_pendingQuantity"),
      style: { minWidth: "10rem" },
      cell: (row: any) => getPendingProductCount(row),
    },
    {
      field: "userName",
      header: t("orderlist_table_username"),
      style: { minWidth: "10rem" },
      cell: (row: any) => row.userName,
    },
    {
      field: "mobileNumb",
      header: t("orderlist_table_mobile"),
      style: { minWidth: "8rem" },
      cell: (row: any) => row.mobileNumb,
    },
    {
      header: "Action",
      style: { minWidth: "10rem" },
      cell: (row: any) => (
        <div>
          {row.orderID && (
            <Button
              icon="pi pi-shopping-cart"
              onClick={() => handleOpenOrderReceipt(row.orderID)}
              style={{ border: "none", color: "#0d9488", background: "none" }}
              className="p-button-rounded"
              tooltip="Open Order Receipt"
            />
          )}
          {row.invoiceNo && (
            <Button
              icon="pi pi-external-link"
              onClick={() => handleOpenReceipt(row.invoiceNo)}
              style={{ border: "none", color: "#0d9488", background: "none" }}
              className="p-button-rounded"
              tooltip="Open Receipt"
            />
          )}
          {row.invoiceNo && (
            <Button
              icon="pi pi-whatsapp"
              onClick={() => sendInvoicedWhatsAppMessage(row)}
              disabled={sendingInvoiceMessageOrderIds.includes(row.orderID)}
              loading={sendingInvoiceMessageOrderIds.includes(row.orderID)}
              style={{ border: "none", color: "#25D366", background: "none" }}
              className="p-button-rounded p-button-text"
              tooltip="Send invoiced message on WhatsApp"
              aria-label={`Send invoiced message to ${row.userName} on WhatsApp`}
            />
          )}
          {row.orderStatusID === 1 && !row.invoiceNo && (
            <Button
              icon="pi pi-delete-left"
              onClick={() => handleCancelOrder(row)}
              style={{ border: "none", color: "tomato", background: "none" }}
              className="p-button-rounded"
              tooltip="Cancel Order"
            />
          )}
          {(row.orderStatusID === 1 || row.orderStatusID === 2) && !row.invoiceNo && (
            <Button
              icon="pi pi-user-plus"
              aria-label="Assign Order"
              onClick={() => handleAssignOrder(row)}
              style={{ border: "none", color: "green", background: "none", marginLeft: "0.1rem" }}
              className="p-button-rounded"
              tooltip="Assign Order"
            />
          )}
          <OrderPaymentLinkButton orderId={row.orderID} customerName={row.userName}
            eligible={paymentLinkOrderIds.has(row.orderID) && [1, 2].includes(row.orderStatusID) && !row.invoiceNo}
            notify={showMessage} onEligibilityChanged={removePaymentLinkEligibility} />
         {row.orderStatusID != 4 && row.orderStatusID != 6 && (
            <Button
              icon="pi pi-comment"
              onClick={() => openCommentDialog(row)}
              style={{ border: "none", color: "#2196F3", background: "none", marginLeft: "0.1rem" }}
              className="p-button-rounded"
              tooltip="Add Comment"
            />
          )}
        </div>
      ),
    },
  ];

  const hideDialog = () => {
    setVisible(false);
    setDialogHeader(t("orderlist_orderdetails_label"));
  };
 
  useEffect(() => {
    setOrderDataList(data || []);
  }, [data]);
  
  const footerContent = (
    <div>
      <Button label="Close" icon="pi pi-times" onClick={() => setVisible(false)} className="p-button-text" />
      <Button label="Print" icon="pi pi-print" onClick={() => setPrintEnabled(true)} autoFocus />
    </div>
  );
 
  const handleTriggerPrint = (val: boolean) => {
    setPrintEnabled(val);
  };
 
  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="col-12 card border-noround p-2">
        {infoMessage && (
            <div className="flex justify-content-start mb-2">
                <div className="flex align-items-center gap-2 p-2 border-round" 
                    style={{  backgroundColor: 'var(--blue-50)',  border: '1px solid var(--blue-100)', display: 'inline-flex', maxWidth: 'fit-content' }}>
                    <i className="pi pi-info-circle" style={{ color: 'var(--blue-600)', fontSize: '1.25rem' }} />
                    <span style={{ color: 'var(--blue-700)' }}>{infoMessage}</span>
                </div>
            </div>
        )}
        
      {data !== null ?<DataTable
           value={data}
           paginator
           rows={pageSize}
           first={(pageNumber - 1) * pageSize}
           totalRecords={totalRecords}
           rowsPerPageOptions={[5, 10, 25, 50, 100]}
           stripedRows
           showGridlines
           emptyMessage="No order found."
           sortMode="multiple"
           paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
           currentPageReportTemplate="{first} to {last} of {totalRecords}"
           onPage={onPageChange}
           lazy
          >
            {columns.map((col, i) => (
              <Column
                key={i}
                field={col.field}
                sortable={col.sortable}
                header={col.header}
                style={col.style}
                body={col.cell}
              />
            ))}
          </DataTable> : ""}
      </div>
      <UserDeleteDialog
        messageHeader={notifyHeader}
        notifyMessage={notifyMessage}
        isVisible={isNotifyVisible}
        isHide={hideNotifyDialog}
      />
      {orderDetails !== undefined || orderDetails !== null ? (
        <Dialog
        header={dialogHeader}
        visible={visible}
        style={{ width: "67vw" }}
        onHide={hideDialog}
        >
          <TabView>
            <TabPanel header="Order Details" leftIcon="pi pi-file mr-1">
              <NewOrderReview TriggerPrint={handleTriggerPrint} PrintEnabled={printEnabled} OrderData={<OrderPrint orderinfo={orderDetails}/>} />
            </TabPanel>
            <TabPanel header="Order Print" leftIcon="pi pi-print mr-1">
            <OrderPrint orderinfo={orderDetails} />
            </TabPanel>
          </TabView>
        </Dialog>
      ) : null}

      <OrderDeliveryAssignmentDialog orderId={selectedOrderId} visible={assignDialogVisible}
        onHide={() => setAssignDialogVisible(false)} onAssigned={onOrderAssigned} notify={showMessage} />

      <Dialog
        header="Add Comment"
        visible={commentDialog}
        style={{ width: "40vw" }}
        modal
        onHide={() => setCommentDialog(false)}
      >
        <div className="p-field">
          <label>Enter Comment:</label>
          <InputTextarea
            rows={4}
            autoResize
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            className="w-full"
            style={{
                border: "2px solid #9bd39dff",
                borderRadius: "4px",
                padding: "0.5rem",
                boxShadow: "none",
                outline: "none",
              }}
          />
        </div>

        <div className="flex justify-content-end gap-2 mt-3">
          <Button label="Cancel" onClick={() => setCommentDialog(false)} className="p-button-text" />
          <Button label="Save" onClick={submitComment} />
        </div>
      </Dialog>

    </>
  );
};
export default OrderListDataTable;
